import { SessionListener } from "../../core/internal/session/SessionListener"
import { User } from "../../core/internal/model/model"
import { CampaignParser } from "./CampaignParser"
import { Campaign, EMPTY_CAMPAIGN, isNewCampaign } from "./Campaign"
import { CampaignStorage } from "./CampaignStorage"
import { PropertyOperationsBuilder } from "../property/PropertyOperations"
import { UserManager } from "../../core/internal/user/UserManager"

export class CampaignManager implements SessionListener {
  constructor(
    private readonly storage: CampaignStorage,
    private readonly parser: CampaignParser,
    private readonly userManager: UserManager
  ) {}

  startNewCampaign(user: User, campaign: Campaign, timestamp: number, isInitial: boolean = false): void {
    const campaignProperties: Campaign = {
      ...EMPTY_CAMPAIGN,
      ...campaign
    }

    // last_* keeps the previous touch through utm-less visits, so a campaign with no utm leaves it untouched.
    const hasNonEmptyUtm = Object.entries(campaignProperties).some(([key, value]) => isUtmKey(key) && value)

    const campaignOperationsBuilder = new PropertyOperationsBuilder()
    const initialOperationsBuilder = new PropertyOperationsBuilder()
    for (let [key, value] of Object.entries(campaignProperties)) {
      if (value) {
        campaignOperationsBuilder.set(key, value)
      } else {
        campaignOperationsBuilder.unset(key)
      }

      if (isUtmKey(key)) {
        if (isInitial) {
          initialOperationsBuilder.setOnce(`initial_${key}`, value || "EMPTY")
        }

        if (hasNonEmptyUtm) {
          if (value) {
            campaignOperationsBuilder.set(`last_${key}`, value)
          } else {
            campaignOperationsBuilder.unset(`last_${key}`)
          }
        }
      }
    }

    // The server applies userProperties as OVERWRITE with higher priority than event properties ($setOnce).
    // Keep initial_* server-only so local userProperties on subsequent events cannot overwrite existing $setOnce values.
    this.userManager.updateUserProperties(
      campaignOperationsBuilder.build(),
      isInitial ? { serverOnlyOperations: initialOperationsBuilder.build() } : undefined
    )
  }

  startNewCampaignIfNeeded(user: User, timestamp: number): void {
    const previousCampaign = this.storage.get()
    const newCampaign = this.parser.parse()
    const domain = this.parser.parseDomain()
    if (isNewCampaign(previousCampaign, newCampaign, domain)) {
      this.storage.set(newCampaign)
      // The campaign cookie is written on the first session of this sdkKey in this browser, so its absence marks the first touch.
      const isInitial = previousCampaign === null
      this.startNewCampaign(user, newCampaign, timestamp, isInitial)
    }
  }

  onSessionEnded(sessionId: string, user: User, timestamp: number): void {}

  onSessionStarted(sessionId: string, user: User, timestamp: number): void {
    this.startNewCampaignIfNeeded(user, timestamp)
  }
}

const isUtmKey = (key: string): boolean => key.startsWith("utm")
